import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { MapPin, Route, ChevronDown } from 'lucide-react';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { TechnicianRoute } from '../types';
import RouteConfirmationMap from './RouteConfirmationMap';
import { CopyableVin } from '@/components/CopyableVin';

interface RouteDetailsPanelProps {
  selectedRoute: TechnicianRoute | null;
  baseLocation?: {
    lat: number;
    lng: number;
    dealerName: string;
    address: string;
  } | null;
}

/* eslint-disable @typescript-eslint/no-explicit-any, react/no-array-index-key, max-len */
const RouteDetailsPanel: React.FC<RouteDetailsPanelProps> = ({ selectedRoute, baseLocation }) => {
  if (!selectedRoute) {
    return (
      <div className='h-full flex items-center justify-center'>
        <Card className='w-full max-w-md'>
          <CardContent className='p-8 text-center'>
            <Route className='w-12 h-12 text-gray-400 mx-auto mb-4' />
            <h3 className='text-lg font-medium text-gray-900 mb-2'>Select a Technician</h3>
            <p className='text-gray-600'>
              Choose a technician from the left panel to view their route details and map.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className='h-full flex flex-col space-y-4'>
      {/* Route Map - 2x size */}
      <Card className='h-[600px] lg:h-[900px]'>
        <CardHeader className='pb-2'>
          <CardTitle className='flex items-center gap-2 text-lg'>
            <MapPin className='w-5 h-5' />
            Route Map
          </CardTitle>
        </CardHeader>
        <CardContent className='p-0 h-[calc(100%-60px)]'>
          <RouteConfirmationMap
            routes={[selectedRoute]}
            dealerLocations={selectedRoute.stops.map(stop => stop.dealer)}
            selectedRouteId={selectedRoute.technician_id}
            baseLocation={
              baseLocation
                ? {
                    lat: baseLocation.lat,
                    lng: baseLocation.lng
                  }
                : null
            }
          />
        </CardContent>
      </Card>

      {/* Route Details */}
      <Card>
        <CardHeader className='pb-3'>
          <CardTitle className='flex items-center gap-2'>
            <div
              className='w-4 h-4 rounded-full'
              style={{ backgroundColor: selectedRoute.route_color }}
            />
            <MapPin className='w-5 h-5' />
            Route Details
          </CardTitle>
        </CardHeader>

        <CardContent className='space-y-4'>
          {/* Start Location */}
          {baseLocation && (
            <div className='space-y-2'>
              <h4 className='font-medium text-sm text-muted-foreground'>Start Location</h4>
              <div className='border rounded-lg p-3 bg-green-50 border-green-200'>
                <div className='flex items-start gap-3'>
                  <div className='w-6 h-6 rounded-full bg-green-500 text-white text-xs flex items-center justify-center font-medium flex-shrink-0'>
                    <MapPin className='w-4 h-4' />
                  </div>
                  <div className='flex-1 min-w-0'>
                    <div className='font-medium text-sm mb-1 text-green-900'>
                      {baseLocation.dealerName}
                    </div>
                    <div className='text-xs text-green-700'>{baseLocation.address}</div>
                    <div className='text-xs text-green-600 mt-1'>
                      📍 {baseLocation.lat.toFixed(4)}, {baseLocation.lng.toFixed(4)}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Route Stops with Details */}
          <div className='space-y-2'>
            <h4 className='font-medium text-sm text-muted-foreground'>Route Stops & Details</h4>
            <div className='space-y-3 max-h-96 overflow-y-auto'>
              {(() => {
                // Group stops by dealer
                const groupedStops = selectedRoute.stops.reduce(
                  (acc, stop) => {
                    const dealerKey = `${stop.dealer.name}-${stop.dealer.city}`;
                    if (!acc[dealerKey]) {
                      acc[dealerKey] = {
                        dealer: stop.dealer,
                        stops: [],
                        stopOrder: stop.stop_order
                      };
                    }
                    acc[dealerKey].stops.push(stop);
                    return acc;
                  },
                  {} as Record<string, { dealer: any; stops: any[]; stopOrder: number }>
                );

                // Sort by stop order
                const sortedGroupedStops = Object.values(groupedStops).sort(
                  (a, b) => a.stopOrder - b.stopOrder
                );

                return sortedGroupedStops.map((group, index) => (
                  <div
                    key={`${selectedRoute?.technician_id}-${group.dealer.id || group.dealer.name}-${index}`}
                    className='border rounded-lg p-3'
                  >
                    <div className='flex items-start gap-3'>
                      <div className='w-6 h-6 rounded-full bg-primary text-primary-foreground text-xs flex items-center justify-center font-medium flex-shrink-0'>
                        {index + 1}
                      </div>
                      <div className='flex-1 min-w-0'>
                        <div className='font-medium text-sm mb-1'>{group.dealer.name}</div>

                        {/* Dealer Address */}
                        <div className='text-xs text-muted-foreground mb-2'>
                          <div>
                            <strong>Address:</strong>{' '}
                            {(() => {
                              // Get first recall from any stop in this group
                              const recall = group.stops[0]?.recalls?.[0];

                              if (recall) {
                                // Extract address components
                                // Note: selected_recalls stores street as 'dealer_address', not 'dealer_street'
                                const street =
                                  recall.dealer_street || (recall as any).dealer_address || '';
                                const city = recall.dealer_city || '';
                                const state = recall.dealer_region || '';
                                const postalCode = recall.dealer_postal_code || '';

                                // Build complete address: street, city, state ZIP
                                const addressParts = [];
                                if (street && street.trim()) addressParts.push(street.trim());
                                if (city && city.trim()) addressParts.push(city.trim());
                                if (state && state.trim()) {
                                  // Append postal code to state (e.g., "TX 75201")
                                  const stateWithZip = postalCode.trim()
                                    ? `${state.trim()} ${postalCode.trim()}`
                                    : state.trim();
                                  addressParts.push(stateWithZip);
                                } else if (postalCode.trim()) {
                                  addressParts.push(postalCode.trim());
                                }

                                if (addressParts.length > 0) {
                                  return addressParts.join(', ');
                                }
                              }

                              return 'Address not available';
                            })()}
                          </div>
                          {group.dealer.phone && (
                            <div>
                              <strong>Phone:</strong> {group.dealer.phone}
                            </div>
                          )}
                        </div>

                        {/* Multiple Cars at Same Dealer - Grouped by VIN */}
                        <Collapsible>
                          <CollapsibleTrigger className='flex items-center gap-1 text-xs font-medium text-muted-foreground hover:text-gray-700'>
                            {(() => {
                              // Count unique VINs
                              const uniqueVins = new Set(
                                group.stops.flatMap(
                                  stop => stop.recalls?.map((r: any) => r.vin) || []
                                )
                              );
                              return <span>Cars to Service ({uniqueVins.size})</span>;
                            })()}
                            <ChevronDown className='w-3 h-3' />
                          </CollapsibleTrigger>
                          <CollapsibleContent className='mt-2 space-y-2'>
                            {(() => {
                              // Group all recalls by VIN
                              const recallsByVin: Record<string, any[]> = {};

                              group.stops.forEach(stop => {
                                if (stop.recalls) {
                                  stop.recalls.forEach((recall: any) => {
                                    const vin = recall.vin;
                                    if (!recallsByVin[vin]) {
                                      recallsByVin[vin] = [];
                                    }
                                    recallsByVin[vin].push(recall);
                                  });
                                }
                              });

                              // Convert to array and sort by VIN for consistent ordering
                              const vinEntries = Object.entries(recallsByVin).sort((a, b) =>
                                a[0].localeCompare(b[0])
                              );

                              return vinEntries.map(([vin, recalls], carIndex) => {
                                const firstRecall = recalls[0];
                                const year = firstRecall?.car_year;
                                const make = firstRecall?.make_name;
                                const model = firstRecall?.model_name;

                                let vehicleDisplay = 'Vehicle Details';
                                if (year && make && model) {
                                  vehicleDisplay = `${year} ${make} ${model}`;
                                } else if (year || make || model) {
                                  vehicleDisplay =
                                    `${year || ''} ${make || ''} ${model || ''}`.trim();
                                }

                                return (
                                  <div
                                    key={`${vin}-${carIndex}`}
                                    className='bg-muted/30 p-2 rounded border-l-2 border-blue-300'
                                  >
                                    <div className='flex items-center gap-2 mb-1'>
                                      <div className='w-5 h-5 rounded-full bg-blue-100 text-blue-700 text-xs flex items-center justify-center font-medium'>
                                        {carIndex + 1}
                                      </div>
                                      <div className='flex-1'>
                                        <div className='text-sm font-medium text-gray-800'>
                                          {vehicleDisplay}
                                        </div>
                                        <div className='text-xs text-gray-500 mt-1'>
                                          <CopyableVin vin={vin} />
                                        </div>
                                      </div>
                                    </div>

                                    {/* Recall Details for this car - Show all recalls */}
                                    {recalls.length > 0 && (
                                      <Collapsible>
                                        <CollapsibleTrigger className='flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 mt-2'>
                                          <span>
                                            Recall Details ({recalls.length} recall
                                            {recalls.length !== 1 ? 's' : ''})
                                          </span>
                                          <ChevronDown className='w-3 h-3' />
                                        </CollapsibleTrigger>
                                        <CollapsibleContent className='mt-2 space-y-2'>
                                          {recalls.map((recall: any, recallIndex: number) => (
                                            <div
                                              key={`${recall.campaign_number}-${recallIndex}`}
                                              className='bg-muted/50 p-2 rounded-lg space-y-1'
                                            >
                                              <div className='flex items-start gap-1'>
                                                <span className='text-xs font-semibold text-gray-600 min-w-[1.25rem]'>
                                                  {recallIndex + 1}.
                                                </span>
                                                <div className='flex-1 space-y-1'>
                                                  <div className='text-sm'>
                                                    <strong>Campaign:</strong>{' '}
                                                    {recall.campaign_number}
                                                  </div>
                                                  <div className='text-sm'>
                                                    <strong>Title:</strong> {recall.recall_title}
                                                  </div>
                                                </div>
                                              </div>
                                            </div>
                                          ))}
                                        </CollapsibleContent>
                                      </Collapsible>
                                    )}
                                  </div>
                                );
                              });
                            })()}
                          </CollapsibleContent>
                        </Collapsible>
                      </div>
                    </div>
                  </div>
                ));
              })()}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default RouteDetailsPanel;
